// The app's navigation (redesign slice 2, wireframes 1 and 23): four tabs,
// at the bottom on a phone and across the top on desktop, plus one menu for
// everything else. Route tables are in src/lib/navigation.ts.
import { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { COLORS, FONT_SIZE, LAYOUT, RADIUS, SPACE } from "../lib/constants";
import { TABS, visibleMenuItems, type MenuItem } from "../lib/navigation";
import { NavIcon } from "./NavIcons";

// Fixed to the bottom of the screen on a phone. The page reserves room for
// it (see AppShell), including the home-indicator inset on newer phones.
export function TabBar() {
  return (
    <nav
      aria-label="Main"
      className="mp-sans"
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 30,
        display: "grid",
        gridTemplateColumns: `repeat(${TABS.length}, minmax(0, 1fr))`,
        background: COLORS.card,
        borderTop: `1px solid ${COLORS.line}`,
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      {TABS.map(({ to, label, icon }) => (
        <NavLink
          key={to}
          to={to}
          className="mp-tab"
          style={({ isActive }) => ({
            minHeight: LAYOUT.tabBarHeight,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: SPACE.hair,
            fontSize: FONT_SIZE.xs,
            fontWeight: isActive ? 700 : 400,
            color: isActive ? COLORS.accent : COLORS.soft,
            textDecoration: "none",
          })}
        >
          <NavIcon name={icon} />
          {label}
        </NavLink>
      ))}
    </nav>
  );
}

export function DesktopTabs() {
  return (
    <nav aria-label="Main" className="mp-sans app-header-tabs" style={{ display: "flex", gap: SPACE.xs }}>
      {TABS.map(({ to, label }) => (
        <NavLink
          key={to}
          to={to}
          className="mp-tab"
          style={({ isActive }) => ({
            minHeight: LAYOUT.tapTarget,
            display: "flex",
            alignItems: "center",
            padding: "0 var(--space-xxl)",
            fontSize: FONT_SIZE.base,
            fontWeight: 700,
            color: isActive ? COLORS.accent : COLORS.soft,
            textDecoration: "none",
            borderBottom: `2px solid ${isActive ? COLORS.accent : "transparent"}`,
            boxSizing: "border-box",
          })}
        >
          {label}
        </NavLink>
      ))}
    </nav>
  );
}

interface AccountMenuProps {
  variant: "phone" | "desktop";
  userEmail: string;
  isAdmin: boolean;
  onSignOut: () => void;
}

// "Me" on a phone, "Settings and account" on desktop. A disclosure (a
// button that shows a list of links), not an ARIA menu, so ordinary Tab
// navigation works inside it. Closes on a route change, a click outside, or
// Escape.
export function AccountMenu({ variant, userEmail, isAdmin, onSignOut }: AccountMenuProps) {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e: PointerEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      buttonRef.current?.focus();
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const items = visibleMenuItems(isAdmin);
  const everyone = items.filter((i) => !i.adminOnly);
  const adminItems = items.filter((i) => i.adminOnly);

  const buttonStyle =
    variant === "phone"
      ? { width: LAYOUT.tapTarget, height: LAYOUT.tapTarget, borderRadius: RADIUS.pill, fontSize: FONT_SIZE.sm }
      : { minHeight: LAYOUT.tapTarget, padding: "0 var(--space-xxl)", borderRadius: RADIUS.pill, fontSize: FONT_SIZE.base };

  return (
    <div ref={wrapperRef} className="mp-sans" style={{ position: "relative", flexShrink: 0 }}>
      <button
        ref={buttonRef}
        type="button"
        className="mp-btn"
        aria-expanded={open}
        aria-controls="account-menu"
        aria-label={variant === "phone" ? "Menu: settings, household, account" : undefined}
        onClick={() => setOpen((o) => !o)}
        style={{ ...buttonStyle, border: `1px solid ${COLORS.line}`, background: COLORS.card, color: COLORS.ink, fontWeight: 700 }}
      >
        {variant === "phone" ? "Me" : "Settings and account"}
      </button>
      {open && (
        <div
          id="account-menu"
          style={{
            position: "absolute",
            top: `calc(100% + var(--space-md))`,
            right: 0,
            zIndex: 40,
            width: 300,
            maxWidth: "calc(100vw - 2 * var(--space-x3))",
            background: COLORS.card,
            border: `1px solid ${COLORS.line}`,
            borderRadius: RADIUS.xl,
            boxShadow: "0 8px 24px rgba(0,0,0,0.16)",
            padding: "var(--space-md) var(--space-xxl)",
            boxSizing: "border-box",
          }}
        >
          <div style={{ padding: "var(--space-md) 0", borderBottom: `1px solid ${COLORS.line}` }}>
            <div style={{ fontSize: FONT_SIZE.sm, color: COLORS.soft }}>Signed in as</div>
            <div style={{ fontSize: FONT_SIZE.base, color: COLORS.ink, overflowWrap: "anywhere" }}>{userEmail}</div>
          </div>
          <MenuLinks items={everyone} />
          {adminItems.length > 0 && (
            <>
              <div style={{ fontSize: FONT_SIZE.xs, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: COLORS.soft, padding: "var(--space-xl) 0 var(--space-xs)" }}>Admin</div>
              <MenuLinks items={adminItems} />
            </>
          )}
          <button
            type="button"
            className="mp-btn"
            onClick={onSignOut}
            style={{ display: "block", width: "100%", textAlign: "left", minHeight: LAYOUT.tapTarget, padding: 0, background: "transparent", color: COLORS.accent, fontSize: FONT_SIZE.base, fontWeight: 700 }}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function MenuLinks({ items }: { items: MenuItem[] }) {
  return (
    <div>
      {items.map(({ to, label }) => (
        <NavLink
          key={to}
          to={to}
          style={({ isActive }) => ({
            display: "flex",
            alignItems: "center",
            minHeight: LAYOUT.tapTarget,
            borderBottom: `1px solid ${COLORS.line}`,
            fontSize: FONT_SIZE.base,
            fontWeight: isActive ? 700 : 400,
            color: isActive ? COLORS.accent : COLORS.ink,
            textDecoration: "none",
          })}
        >
          {label}
        </NavLink>
      ))}
    </div>
  );
}
